import { Bike, Zap, ShoppingBasket, Gift, Tag, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const DailzyECommercePlatform = () => {
  const features = [
    {
      icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Quick Delivery",
      description: "Delivered in 10-20 mins.",
      // Desktop positions (around phone)
      desktopPosition: "top-10 left-0",
      // Mobile positions (on phone, stacked vertically)
      mobilePosition: "top-4 left-4",
      delay: 0
    },
    {
      icon: <ShoppingBasket className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Wide Selection",
      description: "Groceries, electronics & more.",
      desktopPosition: "left-10  transform -translate-y-1/2",
      mobilePosition: "top-20 right-4",
      delay: 0.1
    },
    {
      icon: <Gift className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Exclusive Deals",
      description: "Daily offers and discounts.",
      desktopPosition: "bottom-6 left-0",
      mobilePosition: "top-36 left-4",
      delay: 0.2
    },
    {
      icon: <Tag className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Best Prices",
      description: "Direct from local vendors.",
      desktopPosition: "top-10 right-0",
      mobilePosition: "top-52 right-4",
      delay: 0.3
    },
    {
      icon: <Award className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Quality Assured",
      description: "Fresh and top-grade items.",
      desktopPosition: "right-8  transform -translate-y-1/2",
      mobilePosition: "bottom-32 left-4",
      delay: 0.4
    },
    {
      icon: <Bike className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Eco-friendly",
      description: "Sustainable packaging.",
      desktopPosition: "bottom-10 right-0",
      mobilePosition: "bottom-16 right-4",
      delay: 0.5
    },
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-white overflow-hidden">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12 sm:mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-3 sm:px-4 py-2 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Bike size={16} className="sm:w-5 sm:h-5" />
            <span className="font-semibold text-xs sm:text-sm">E-commerce Platform</span>
          </motion.div>
          <motion.h2 
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-800 mb-4 px-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            viewport={{ once: true }}
          >
            Dailzy <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">E-commerce Platform</span>
          </motion.h2>
          <motion.p 
            className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            viewport={{ once: true }}
          >
            A seamless shopping experience with features designed for your convenience and delight. Experience the future of quick commerce.
          </motion.p>
        </motion.div>
        
        {/* Main Content - Floating Features Layout */}
        <div className="relative flex justify-center items-center">
          {/* Container for phone + floating features */}
          <div className="relative h-[600px] sm:h-[650px] lg:h-[700px] w-full max-w-[900px] flex items-center justify-center">
            
            {/* Phone Mockup - Centered */}
            <motion.div 
              className="relative w-64 sm:w-72 lg:w-80 h-[520px] sm:h-[580px] lg:h-[620px] bg-gray-900 rounded-[2.5rem] sm:rounded-[3rem] p-2 shadow-2xl z-10"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              animate={{
                y: ["-5px", "5px"],
              }}
              style={{
                animationDuration: "4s",
                animationIterationCount: "infinite",
                animationDirection: "alternate",
                animationTimingFunction: "ease-in-out"
              }}
            >
              <div className="w-full h-full bg-white rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden">
                <div className="h-full bg-gray-50 relative">
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-4 sm:px-6 pt-3 sm:pt-4 pb-2 bg-white">
                    <span className="text-xs sm:text-sm font-semibold">3:43</span>
                    <div className="flex items-center gap-1">
                      <div className="flex gap-0.5">
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                      </div>
                      <span className="text-xs">📶</span>
                      <div className="w-4 sm:w-5 h-2 sm:h-2.5 border border-black rounded-sm"></div>
                    </div>
                  </div>

                  {/* Header */}
                  <div className="px-4 sm:px-6 py-3 sm:py-4 bg-white">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div className="flex items-center">
                        <img src="/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png" alt="Dailzy" className="w-6 sm:w-8 h-6 sm:h-8 mr-2" />
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-800">Dailzy</h1>
                      </div>
                      <div className="w-6 sm:w-8 h-6 sm:h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-sm sm:text-lg">🛒</span>
                      </div>
                    </div>
                    
                    {/* Search Bar */}
                    <div className="bg-gray-100 rounded-lg sm:rounded-xl p-2.5 sm:p-3 shadow-inner">
                      <div className="text-gray-400 text-xs sm:text-sm">Search for groceries, vegetables...</div>
                    </div>
                  </div>

                  {/* Order Tracking */}
                  <div className="px-4 sm:px-6 my-3 sm:my-4">
                    <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg sm:rounded-xl p-3 sm:p-4 text-white shadow-lg">
                      <div className="text-xs sm:text-sm opacity-90">Your Order #1234</div>
                      <div className="text-base sm:text-lg font-semibold">Being Prepared</div>
                      <div className="flex items-center mt-2">
                        <div className="w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white rounded-full mr-2"></div>
                        <div className="text-xs sm:text-sm opacity-90">Estimated delivery: 25-30 min</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Categories */}
                  <div className="px-4 sm:px-6">
                    <h2 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Categories</h2>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 text-center shadow-sm">
                        <div className="text-2xl sm:text-3xl mb-1">🥬</div>
                        <div className="text-xs font-medium text-gray-600">Vegetables</div>
                      </div>
                      <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 text-center shadow-sm">
                        <div className="text-2xl sm:text-3xl mb-1">🍎</div>
                        <div className="text-xs font-medium text-gray-600">Fruits</div>
                      </div>
                      <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 text-center shadow-sm">
                        <div className="text-2xl sm:text-3xl mb-1">🥛</div>
                        <div className="text-xs font-medium text-gray-600">Dairy</div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Navigation */}
                  <div className="absolute bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-gray-100 px-4 sm:px-6 py-2 sm:py-3">
                    <div className="flex justify-around">
                      <div className="text-center">
                        <div className="text-red-500 text-lg sm:text-2xl">🏠</div>
                        <div className="text-xs text-red-500 font-semibold">Home</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400 text-lg sm:text-2xl">🔍</div>
                        <div className="text-xs text-gray-400">Search</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400 text-lg sm:text-2xl">🛒</div>
                        <div className="text-xs text-gray-400">Cart</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400 text-lg sm:text-2xl">👤</div>
                        <div className="text-xs text-gray-400">Profile</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Floating Features */}
            {features.map((feature, index) => {
              // Define mobile positions for each feature to avoid overlap
              const mobileClasses = [
                "top-4 left-4",      // Quick Delivery
                "top-20 right-4",    // Wide Selection  
                "top-36 left-4",     // Exclusive Deals
                "top-52 right-4",    // Best Prices
                "bottom-32 left-4",  // Quality Assured
                "bottom-16 right-4"  // Eco-friendly
              ];

              return (
                <motion.div
                  key={index}
                  className={`absolute z-20 ${mobileClasses[index]} lg:${feature.desktopPosition.replace('transform -translate-y-1/2', 'lg:transform lg:-translate-y-1/2')}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    y: ["-5px", "5px"]
                  }}
                  transition={{
                    duration: 3 + index * 0.2,
                    delay: feature.delay,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                >
                  <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg w-36 sm:w-44 lg:w-48 hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 text-red-500 rounded-lg p-2 sm:p-3 inline-block mb-2 sm:mb-3">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{feature.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-tight">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailzyECommercePlatform;
